import React, { useState } from 'react';
import ExternalAgreementList from './ExternalAgreementList';
import { searchExternalAgreements, getExternalAgreementFilterData } from '@/services/external-agreements';
import ResidentialUnitGrouping from '../residential-units/ResidentialUnitGrouping/ResidentialUnitGrouping';
import ResidentialUnitGroupingHeader from '../residential-units/ResidentialUnitGrouping/ResidentialUnitGroupingHeader';
import {
    useDefaultScrollToId,
    useCanScroll,
    useAsyncStateWithIsLoading,
    useAsyncState,
    useQueryState,
    useParamReplace,
} from '@/utils/hooks';
import { BatchedChanges, ExternalAgreement, ExternalAgreementStatus } from '@/types';
import ExternalAgreementFilter, {
    ExternalAgreementFilterValue,
    getInitialExternalAgreementsFilter,
} from './ExternalAgreementFilter';
import { FilterButtons, LoadingIndicator, useIsFilterModified } from '@ygtechnologies/bygr-frontend-component-library';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import { Link } from 'react-router-dom';
import { ButtonBar, SlideToggle, Tabs } from '@ygtechnologies/rubus-ui';

const ALL_STATUS_ID = 'all';

interface ExternalAgreementsFilterableListProps {
    projectId: number;
    status: ExternalAgreementStatus | null;
    mode?: 'normal' | 'tasks' | 'messages';
}

const ExternalAgreementsFilterableList: React.FC<ExternalAgreementsFilterableListProps> = ({ projectId, mode, status }) => {
    const defaultExpandedId = useDefaultScrollToId();
    const canScroll = useCanScroll(defaultExpandedId);
    const threads = useSelector((state: IState) => state.seller.projectOverview.comments);

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filter, setFilter] = useQueryState<ExternalAgreementFilterValue>(getInitialExternalAgreementsFilter());
    const filterModified = useIsFilterModified(filter, getInitialExternalAgreementsFilter);

    const [includeCancelled, setIncludeCancelled] = useState(false);
    const [filterData] = useAsyncState(() => getExternalAgreementFilterData(projectId), [projectId], null);
    const [externalAgreements, setExternalAgreements, , isLoading] = useAsyncStateWithIsLoading(
        async () => {
            const statuses = status
                ? [status]
                : [ExternalAgreementStatus.Approved, ExternalAgreementStatus.Pending, ExternalAgreementStatus.Rejected];

            if (includeCancelled) {
                statuses.push(ExternalAgreementStatus.Canceled);
            }
            const agreements = await searchExternalAgreements({
                projectId,
                approvableOnly: mode === 'tasks',
                residentialUnitIds: filter.units,
                residentialUnitGroupIds: filter.groups,
                statuses,
                searchText: filter.searchText,
            });
            if (mode === 'messages') {
                agreements.sort((a, b) => {
                    const aMessage = threads?.find((x) => x.threadId === a.commentThreadId)?.lastMessageDate;
                    const bMessage = threads?.find((x) => x.threadId === b.commentThreadId)?.lastMessageDate;
                    if (aMessage && bMessage) {
                        var aDate = new Date(aMessage);
                        var bDate = new Date(bMessage);
                        return bDate.getTime() - aDate.getTime();
                    } else {
                        if (aMessage) return -1;
                        if (bMessage) return 1;
                        return 0;
                    }
                });
            }
            return agreements;
        },
        [filter, mode, status, threads, includeCancelled],
        null
    );

    const handleChange = (changes: BatchedChanges<ExternalAgreement>) => {
        setExternalAgreements((v) => changes.updateArray(v!));
    };

    const replaceParams = useParamReplace<{ status?: string }>();
    const { _ } = useLingui();

    const excludeValues =
        mode === 'tasks'
            ? [ExternalAgreementStatus.Approved, ExternalAgreementStatus.Canceled]
            : [ExternalAgreementStatus.Canceled];
    const statusValues = Object.values(ExternalAgreementStatus).filter((x) => !excludeValues.includes(x));
    const showAllOption = mode !== 'tasks';
    const activeStatusId = status ?? ALL_STATUS_ID;

    return (
        <>
            <ButtonBar sticky margins="none" stickyClassName="stickyBleed">
                <Tabs activeId={activeStatusId} variant="pill" size="sm">
                    {showAllOption && (
                        <Tabs.Tab
                            id={ALL_STATUS_ID}
                            as={Link}
                            to={replaceParams({ status: undefined })}
                            label={<Trans>All</Trans>}
                        />
                    )}
                    {statusValues.map((value) => (
                        <Tabs.Tab
                            key={value}
                            id={value}
                            as={Link}
                            to={replaceParams({ status: value.toLowerCase() })}
                            label={_({ id: `ExternalAgreementStatus.${value}` })}
                        />
                    ))}
                </Tabs>

                <ButtonBar.Right>
                    <SlideToggle onChange={() => setIncludeCancelled(!includeCancelled)} checked={includeCancelled}>
                        <Trans>Show cancelled agreements</Trans>
                    </SlideToggle>
                    <ExternalAgreementFilter
                        onClear={() => setFilter(getInitialExternalAgreementsFilter())}
                        isOpen={isFilterOpen}
                        onClose={() => setIsFilterOpen(false)}
                        filterData={filterData}
                        value={filter}
                        onFilterChange={setFilter}
                        hideStatuses
                    />

                    <FilterButtons
                        searchText={filter.searchText}
                        onSearchTextChange={(v) => setFilter({ ...filter, searchText: v })}
                        isLoading={isLoading}
                        onButtonClick={() => setIsFilterOpen((v) => !v)}
                        isActive={filterModified}
                    />
                </ButtonBar.Right>
            </ButtonBar>

            {externalAgreements ? (
                <>
                    {mode === 'messages' && (
                        <ExternalAgreementList
                            defaultExpandedId={canScroll ? defaultExpandedId : undefined}
                            externalAgreements={externalAgreements}
                            onChange={handleChange}
                            showUnit={true}
                        />
                    )}
                    {mode !== 'messages' && (
                        <ResidentialUnitGrouping
                            projectId={projectId}
                            items={externalAgreements}
                            emptyText={
                                <ExternalAgreementList showUnit={false} externalAgreements={[]} onChange={() => {}} />
                            }>
                            {(items, residentialUnit, residentialUnitGroup) => (
                                <React.Fragment key={residentialUnit.id}>
                                    <ResidentialUnitGroupingHeader
                                        residentialUnit={residentialUnit}
                                        residentialUnitGroup={residentialUnitGroup}
                                        detailsLinkTab="external-agreements"
                                    />
                                    <ExternalAgreementList
                                        showUnit={false}
                                        defaultExpandedId={canScroll ? defaultExpandedId : undefined}
                                        externalAgreements={items}
                                        onChange={handleChange}
                                    />
                                </React.Fragment>
                            )}
                        </ResidentialUnitGrouping>
                    )}
                </>
            ) : (
                <LoadingIndicator />
            )}
        </>
    );
};

export default ExternalAgreementsFilterableList;
